import _ from 'underscore';
import { Set } from '../../models/Set';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { ActionDialogBodyWithInlineErrorMessageProps, ActionDialogBodyWithInlineErrorMessageState, ActionDialogBodyWithInlineErrorMessage } from '~commonInfra/dialogs/ActionDialogBodyWithInlineErrorMessage';
import { ISiteSettingsForServerAdmin } from '../../../settings/types/ISite';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance } from '../../../sessionManagement/SessionManagementService';
import { L10n } from '../../L10n/L10n';
import { SiteSwitchService } from './SiteSwitchService';
import { LoginUrlsWrapper } from '../../../app/spaces/Login/LoginUrls';
import { Sites } from './resources/Sites';
import { IActionInfo } from '../../ServerApi/types/IActionInfo';
import { IError } from '../../../httpClient/serverApi/types/IError';
import { SiteSettingsErrorCode } from '../../helpers/ErrorCodes';
import { Messages } from '../../../codegen/Localize';
import { ToasterService, ToastType } from '../../services/ToasterService';

const initialDialogState: ActionDialogBodyWithInlineErrorMessageState = {
};

function execute(sites: Set<ISiteSettingsForServerAdmin>): Promise<IResult> {
  const server = getSessionManagementServiceInstance();
  const initialBodyProps: ActionDialogBodyWithInlineErrorMessageProps =
  {
    dialogMessage: Messages.confirmDeleteSites({
      ITEM_COUNT: sites.size(),
      ITEM_NAME: L10n.elideIfLong(sites.first().settings.name),
      ITEM_COUNT_LOC: L10n.localizedCount(sites.size())
    })
  };
  const dialogInstance = openReactActionDialog({
    title: Messages.deleteMessage(),
    actionName: Messages.confirmDeleteButton({
      ITEM_COUNT: sites.size(),
      ITEM_COUNT_LOC: L10n.localizedCount(sites.size())
    }),
    isActionDelete: true,
    action: (bodyProps, state, updateDialogState: React.Dispatch<ActionDialogBodyWithInlineErrorMessageState>) => {
      const isAttemptingToDeleteCurrentSite = server.sessionSiteUrlName() !== ''
        && !_.isUndefined(sites.find((site) => server.isCurrentSiteUrlName(site.settings.urlName)));

      if (isAttemptingToDeleteCurrentSite) {
        return SiteSwitchService.switchIfNeededNoFeedback('')
          .then(
            () => deleteSites(sites, updateDialogState),
            () => deleteSites(sites, updateDialogState).then(() => {
              LoginUrlsWrapper.navigateToSignIn({});
            })
          );
      } else {
        return deleteSites(sites, updateDialogState);
      }
    },
    dialogWidth: TabDialogWidth.width375px,
    bodyComponent: ActionDialogBodyWithInlineErrorMessage,
    dismissDialogOnActionFailed: false,
    initialBodyProps,
    initialDialogState
  });

  return Promise.resolve(dialogInstance.result);
}

function deleteSites(sites: Set<ISiteSettingsForServerAdmin>, updateDialogState: React.Dispatch<ActionDialogBodyWithInlineErrorMessageState>): Promise<any> {
  return Sites.instance.del(sites.keys()).then(
    (actionResult: IActionInfo) => onComplete(actionResult, sites, updateDialogState),
    (errors: IError[]) => onFail(errors, sites, updateDialogState),
  );
}

function onComplete(actionResult: IActionInfo, sites: Set<ISiteSettingsForServerAdmin>, updateDialogState: React.Dispatch<ActionDialogBodyWithInlineErrorMessageState>): Promise<any> {
  if (actionResult && actionResult.succeeded === false) {
    return onFail(actionResult.response && actionResult.response.errors, sites, updateDialogState);
  } else {
    ToasterService.instance.pop({
      type: ToastType.Info,
      text: Messages.deleteSitesSuccess({
        ITEM_COUNT: sites.size(),
        ITEM_COUNT_LOC: L10n.localizedCount(sites.size())
      })
    });
    // Refresh session info in case we switched from multi-site to single-site
    getSessionManagementServiceInstance().refreshSessionInfo();
    return Promise.resolve();
  }
}

function onFail(errors: IError[], sites: Set<ISiteSettingsForServerAdmin>, updateDialogState: React.Dispatch<ActionDialogBodyWithInlineErrorMessageState>): Promise<any> {
  let successCount = sites.size();

  if (errors && errors.length) {
    successCount -= errors.length;
    const attemptedToDeleteDefaultSiteError = _.find(errors, (e) => e.code === SiteSettingsErrorCode.AttemptedToDeleteDefaultSite);
    // Error toast if attempted to delete default site
    if (attemptedToDeleteDefaultSiteError) {
      ToasterService.instance.pop({ type: ToastType.Error, text: Messages.siteSettingsFailedAttemptedToDeleteDefaultSite() });
    }
    // Inline error message if there are errors besides attempting to delete default site
    if (errors.length > 1 || !attemptedToDeleteDefaultSiteError) {
      updateDialogState({
        errorMessage: Messages.deleteSitesFailed({
          ITEM_COUNT: errors.length,
          ITEM_COUNT_LOC: L10n.localizedCount(errors.length)
        })
      });
      return Promise.reject();
    }
    // If there was a partial success, display it in a success toast
    if (successCount > 0) {
      ToasterService.instance.pop({
        type: ToastType.Info,
        text: Messages.deleteSitesSuccess({
          ITEM_COUNT: successCount,
          ITEM_COUNT_LOC: L10n.localizedCount(successCount)
        })
      });
      // Refresh session info in case we switched from multi-site to single-site
      getSessionManagementServiceInstance().refreshSessionInfo();
    } else {
      // If we have a failure with no errors, assume all delete actions failed
      updateDialogState({
        errorMessage: Messages.deleteSitesFailed({
          ITEM_COUNT: sites.size(),
          ITEM_COUNT_LOC: L10n.localizedCount(sites.size())
        })
      });
      return Promise.reject();
    }
    return Promise.resolve();
  }
}

export const DeleteSitesAction = {
  execute
};
